import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Snackbar } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faPen } from "@fortawesome/free-solid-svg-icons";
import { ACCESS_MODES } from "../../utils/apiTarget";
import { getDbInterfaceRoutePath } from "../dbInterfaceConfig";
import { useDbInterfaceSessionContext } from "./dbInterfaceSessionGate";
import {
  SESSION_ENDED_REASONS,
  SESSION_WARNING_THRESHOLD_MILLISECONDS,
  formatRemainingDuration,
  formatSessionEndTime,
} from "./dbInterfaceSessionTiming";

// Header strip for both DB Interface links: which link this is, who is signed in, when the
// session ends, and a Logout button.
const DbInterfaceSessionBar = ({ accessMode, isDev, permissionsLoaded, canWrite }) => {
  const session = useDbInterfaceSessionContext();
  const [warningShown, setWarningShown] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);

  const isReadWriteLink = accessMode === ACCESS_MODES.readWrite;

  // The countdown ticks here, in the only component that displays it. Keeping this in the
  // session context re-rendered the entire page, table included, once a second.
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (!session.sessionEndsAt) {
      setRemaining(null);
      return undefined;
    }
    const updateRemaining = () => setRemaining(Math.max(0, session.sessionEndsAt - Date.now()));
    updateRemaining();
    const intervalId = setInterval(updateRemaining, 1000);
    return () => clearInterval(intervalId);
  }, [session.sessionEndsAt]);

  const isEndingSoon = remaining !== null && remaining <= SESSION_WARNING_THRESHOLD_MILLISECONDS;

  useEffect(() => {
    if (isEndingSoon && !warningShown && remaining > 0) {
      setWarningShown(true);
      setWarningOpen(true);
    }
  }, [isEndingSoon, warningShown, remaining]);

  const readWritePath = getDbInterfaceRoutePath(ACCESS_MODES.readWrite, isDev) + window.location.search;
  const readOnlyPath = getDbInterfaceRoutePath(ACCESS_MODES.readOnly, isDev) + window.location.search;

  return (
    <Bar>
      {isReadWriteLink ? (
        <LinkBadge tone="write">
          <FontAwesomeIcon icon={faPen} style={{ marginRight: 6 }} />
          Read-write link · VPN
          {permissionsLoaded && !canWrite && <Sub> · your account has read-only permission</Sub>}
          <BadgeLink href={readOnlyPath}>Open read-only</BadgeLink>
        </LinkBadge>
      ) : (
        <LinkBadge tone="read">
          <FontAwesomeIcon icon={faLock} style={{ marginRight: 6 }} />
          Read-only link
          <BadgeLink href={readWritePath}>Open read-write (VPN required)</BadgeLink>
        </LinkBadge>
      )}

      {session.isDevelopmentTokenSession ? (
        <Muted>Dev token session</Muted>
      ) : (
        <>
          {session.user?.email && <Muted>{session.user.email}</Muted>}
          {session.sessionEndsAt && (
            <Countdown warn={isEndingSoon}>
              Session ends at {formatSessionEndTime(session.sessionEndsAt)} ({formatRemainingDuration(remaining)})
            </Countdown>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => session.endSession(SESSION_ENDED_REASONS.userLoggedOut)}
          >
            Logout
          </Button>
        </>
      )}

      <Snackbar
        open={warningOpen}
        autoHideDuration={8000}
        onClose={() => setWarningOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert elevation={6} variant="filled" severity="warning" onClose={() => setWarningOpen(false)}>
          Your session ends in 5 minutes. Finish your edits and sign in again afterwards.
        </MuiAlert>
      </Snackbar>
    </Bar>
  );
};

export default DbInterfaceSessionBar;

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 6px 12px;
  font-size: 13px;
  border-bottom: 1px solid #e5e5e5;
  background: #fafafa;
`;

const LinkBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 14px;
  font-weight: 500;
  background: ${({ tone }) => (tone === "write" ? "#fbe6e4" : "#f0f0f0")};
  color: ${({ tone }) => (tone === "write" ? "#b3261e" : "#555")};
  border: 1px solid ${({ tone }) => (tone === "write" ? "#f1b9b4" : "#ddd")};
`;

const Sub = styled.span`
  font-weight: 400;
`;

const BadgeLink = styled.a`
  margin-left: 8px;
  color: #1976d2;
  text-decoration: none;
  font-weight: 400;
  &:hover { text-decoration: underline; }
`;

const Muted = styled.span`
  color: #666;
`;

const Countdown = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${({ warn }) => (warn ? "#c77700" : "#444")};
  font-weight: ${({ warn }) => (warn ? 600 : 400)};
`;
